import { useCallback, useEffect, useRef, useState } from 'react';
import {
  projectService,
  type ScopeDto,
  type ScopePaginationMeta,
  type ScopeSortBy,
  type ScopeSortOrder,
} from '@ideoz/core';

/** How many scopes are requested per page (backend caps at 100). */
export const SCOPES_PAGE_SIZE = 10;

/** A combined sort choice surfaced in the UI, mapped to the API's field+order. */
export type ScopeSortOption =
  | 'newest'
  | 'oldest'
  | 'recentlyUpdated'
  | 'iceHigh'
  | 'iceLow';

const SORT_MAP: Record<ScopeSortOption, { sortBy: ScopeSortBy; sortOrder: ScopeSortOrder }> = {
  newest: { sortBy: 'createdAt', sortOrder: 'DESC' },
  oldest: { sortBy: 'createdAt', sortOrder: 'ASC' },
  recentlyUpdated: { sortBy: 'updatedAt', sortOrder: 'DESC' },
  iceHigh: { sortBy: 'iceScore', sortOrder: 'DESC' },
  iceLow: { sortBy: 'iceScore', sortOrder: 'ASC' },
};

export interface UseScopesListResult {
  scopes: ScopeDto[];
  pagination: ScopePaginationMeta | null;
  isLoading: boolean;
  error: string | null;
  /** Raw search text in the input (does not trigger a fetch on its own). */
  search: string;
  setSearch: (value: string) => void;
  /** Apply the current input as the active query (call on Enter). */
  submitSearch: () => void;
  /** Clear both the input and the active query, refetching immediately. */
  clearSearch: () => void;
  /** Whether an actual (submitted) search term is filtering the list. */
  isSearchActive: boolean;
  sort: ScopeSortOption;
  setSort: (option: ScopeSortOption) => void;
  page: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

/**
 * Server-driven scopes list: owns the page / search / sort query state and
 * fetches `GET /projects/:id/scopes` via `projectService.listScopes`. The search
 * input is applied on submit (Enter), not on every keystroke; changing the
 * applied search or the sort resets to page 1, and a request-id guard drops
 * stale responses.
 */
export function useScopesList(startupId: string | null | undefined): UseScopesListResult {
  const [scopes, setScopes] = useState<ScopeDto[]>([]);
  const [pagination, setPagination] = useState<ScopePaginationMeta | null>(null);
  // Start loading when there's a startup to fetch, so the first paint shows
  // skeletons rather than a one-frame flash of the empty state.
  const [isLoading, setIsLoading] = useState<boolean>(!!startupId);
  const [error, setError] = useState<string | null>(null);

  // `search` is what's typed; `activeSearch` is what the last submit applied and
  // is the only one that drives the request.
  const [search, setSearchState] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [sort, setSortState] = useState<ScopeSortOption>('newest');
  const [page, setPage] = useState(1);

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
  }, []);

  const submitSearch = useCallback(() => {
    setSearchState((current) => {
      const next = current.trim();
      setActiveSearch(next);
      setPage(1);
      return current;
    });
  }, []);

  const clearSearch = useCallback(() => {
    setSearchState('');
    setActiveSearch('');
    setPage(1);
  }, []);

  const setSort = useCallback((option: ScopeSortOption) => {
    setSortState(option);
    setPage(1);
  }, []);

  const reqId = useRef(0);
  const fetchList = useCallback(async () => {
    if (!startupId) {
      setScopes([]);
      setPagination(null);
      return;
    }
    const id = ++reqId.current;
    setIsLoading(true);
    setError(null);
    try {
      const { sortBy, sortOrder } = SORT_MAP[sort];
      const res = await projectService.listScopes(startupId, {
        page,
        limit: SCOPES_PAGE_SIZE,
        search: activeSearch || undefined,
        sortBy,
        sortOrder,
      });
      if (id !== reqId.current) return; // a newer request superseded this one
      setScopes(res.scopes);
      setPagination(res.pagination);
    } catch (err: any) {
      if (id !== reqId.current) return;
      setError(err?.message ?? 'Failed to load scopes');
      setScopes([]);
      setPagination(null);
    } finally {
      if (id === reqId.current) setIsLoading(false);
    }
  }, [startupId, page, activeSearch, sort]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  return {
    scopes,
    pagination,
    isLoading,
    error,
    search,
    setSearch,
    submitSearch,
    clearSearch,
    isSearchActive: activeSearch.length > 0,
    sort,
    setSort,
    page,
    setPage,
    refetch: () => {
      void fetchList();
    },
  };
}
